import { left, right } from '@sweet-monads/either';
import type { Either } from '@sweet-monads/either';

import { AbstractError } from '@/domain/errors/abstract-error.js';
import { ServerError } from '@/domain/errors/server-error.js';
import type {
    BusinessProcessResponse,
    CreateBusinessProcessParams,
    CreateProjectParams,
    ProjectDocResponse,
    ProjectModel,
    ProjectOptionModel,
    ProjectUserResponse,
    UpdateBusinessProcessParams,
    UpdateProjectParams,
    UploadDocumentParams
} from '@/domain/models/project.js';
import type { ProjectRepository } from '@/domain/repositories/project-repository.js';
import type {
    CreateBusinessProcess,
    CreateProject,
    DeleteBusinessProcess,
    DeleteProjectDocument,
    GetProjectDocumentDownloadUrl,
    ListActiveBusinessProcesses,
    ListBusinessProcesses,
    ListProjectDocuments,
    ListProjectUsers,
    LoadMyProjects,
    LoadProjectById,
    LoadProjects,
    ToggleBusinessProcess,
    UpdateBusinessProcess,
    UpdateProject,
    UpdateProjectStatus,
    UploadProjectDocument
} from '@/domain/use-cases/projects.js';
import type { ProjectStatus } from '@/shared/utils/format-status.js';

export class LoadProjectsUseCase implements LoadProjects {
    constructor(private readonly repository: ProjectRepository) {}

    public async execute(params?: { search?: string; status?: string; clientId?: string }): Promise<Either<AbstractError, ProjectModel[]>> {
        try {
            return right(await this.repository.findAll(params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class LoadProjectByIdUseCase implements LoadProjectById {
    constructor(private readonly repository: ProjectRepository) {}

    public async execute(id: string): Promise<Either<AbstractError, ProjectModel>> {
        try {
            return right(await this.repository.findById(id));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class CreateProjectUseCase implements CreateProject {
    constructor(private readonly repository: ProjectRepository) {}

    public async execute(params: CreateProjectParams): Promise<Either<AbstractError, ProjectModel>> {
        try {
            return right(await this.repository.create(params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class UpdateProjectUseCase implements UpdateProject {
    constructor(private readonly repository: ProjectRepository) {}

    public async execute(id: string, params: UpdateProjectParams): Promise<Either<AbstractError, ProjectModel>> {
        try {
            return right(await this.repository.update(id, params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class UpdateProjectStatusUseCase implements UpdateProjectStatus {
    constructor(private readonly repository: ProjectRepository) {}

    public async execute(id: string, status: ProjectStatus): Promise<Either<AbstractError, void>> {
        try {
            return right(await this.repository.updateStatus(id, status));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class LoadMyProjectsUseCase implements LoadMyProjects {
    constructor(private readonly repository: ProjectRepository) {}

    public async execute(): Promise<Either<AbstractError, ProjectOptionModel[]>> {
        try {
            return right(await this.repository.findMyProjects());
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class ListBusinessProcessesUseCase implements ListBusinessProcesses {
    constructor(private readonly repository: ProjectRepository) {}

    public async execute(projectId: string): Promise<Either<AbstractError, BusinessProcessResponse[]>> {
        try {
            return right(await this.repository.listBusinessProcesses(projectId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class ListActiveBusinessProcessesUseCase implements ListActiveBusinessProcesses {
    constructor(private readonly repository: ProjectRepository) {}

    public async execute(projectId: string): Promise<Either<AbstractError, BusinessProcessResponse[]>> {
        try {
            return right(await this.repository.listActiveBusinessProcesses(projectId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class ListProjectUsersUseCase implements ListProjectUsers {
    constructor(private readonly repository: ProjectRepository) {}

    public async execute(projectId: string, profile?: string): Promise<Either<AbstractError, ProjectUserResponse[]>> {
        try {
            return right(await this.repository.listProjectUsers(projectId, profile));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class CreateBusinessProcessUseCase implements CreateBusinessProcess {
    constructor(private readonly repository: ProjectRepository) {}

    public async execute(projectId: string, params: CreateBusinessProcessParams): Promise<Either<AbstractError, BusinessProcessResponse>> {
        try {
            return right(await this.repository.createBusinessProcess(projectId, params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class UpdateBusinessProcessUseCase implements UpdateBusinessProcess {
    constructor(private readonly repository: ProjectRepository) {}

    public async execute(projectId: string, processId: string, params: UpdateBusinessProcessParams): Promise<Either<AbstractError, BusinessProcessResponse>> {
        try {
            return right(await this.repository.updateBusinessProcess(projectId, processId, params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class ToggleBusinessProcessUseCase implements ToggleBusinessProcess {
    constructor(private readonly repository: ProjectRepository) {}

    public async execute(projectId: string, processId: string, isActive: boolean): Promise<Either<AbstractError, void>> {
        try {
            await this.repository.toggleBusinessProcess(projectId, processId, isActive);
            return right(undefined);
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class DeleteBusinessProcessUseCase implements DeleteBusinessProcess {
    constructor(private readonly repository: ProjectRepository) {}

    public async execute(projectId: string, processId: string): Promise<Either<AbstractError, void>> {
        try {
            await this.repository.deleteBusinessProcess(projectId, processId);
            return right(undefined);
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class ListProjectDocumentsUseCase implements ListProjectDocuments {
    constructor(private readonly repository: ProjectRepository) {}

    public async execute(projectId: string): Promise<Either<AbstractError, ProjectDocResponse[]>> {
        try {
            return right(await this.repository.listDocuments(projectId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class UploadProjectDocumentUseCase implements UploadProjectDocument {
    constructor(private readonly repository: ProjectRepository) {}

    public async execute(projectId: string, params: UploadDocumentParams): Promise<Either<AbstractError, ProjectDocResponse>> {
        try {
            return right(await this.repository.uploadDocument(projectId, params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class DeleteProjectDocumentUseCase implements DeleteProjectDocument {
    constructor(private readonly repository: ProjectRepository) {}

    public async execute(projectId: string, docId: string): Promise<Either<AbstractError, void>> {
        try {
            await this.repository.deleteDocument(projectId, docId);
            return right(undefined);
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class GetProjectDocumentDownloadUrlUseCase implements GetProjectDocumentDownloadUrl {
    constructor(private readonly repository: ProjectRepository) {}

    public async execute(projectId: string, docId: string): Promise<Either<AbstractError, { url: string }>> {
        try {
            return right(await this.repository.getDocumentDownloadUrl(projectId, docId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}
